import { forwardRef, useState, useMemo } from "react";
import { Alert, Box, Collapse, Typography } from "@mui/material";
import { BellRing, Bot, ChevronDown, Handshake } from "lucide-react";
import { StepProps, StepRef } from "../../types";
import { NextStepButton } from "../../components/NextStepButton";
import TextField from "components/common/TextField";
import PhoneInput from "components/creation/PhoneInput";
import { cleanPhone } from "common/utils";
import { checkPhone } from "common/userUtils";
import useIsMobile from "components/hooks/useIsMobile";
import { useLocation } from "react-router-dom";

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
}

interface FormErrors {
  [key: string]: string;
}

const features = [
  {
    icon: <Bot size={22} />,
    title: "Your personalized AI agent",
    detail:
      "Share your calendar, context, and preferences — your agent handles the rest.",
  },
  {
    icon: <BellRing size={22} />,
    title: "Smart reminders & follow-up",
    detail:
      "Your agent checks calendars, reviews documents, and manages routines on its own.",
  },
  {
    icon: <Handshake size={22} />,
    title: "Human help when you need it",
    detail:
      "For tasks that need judgment or extra research, we route them through our trusted assistants.",
  },
];

export default forwardRef<StepRef, StepProps>(
  ({ onNext, signupState, disabled }, _ref) => {
    const isMobile = useIsMobile();
    const location = useLocation();
    const phoneFromParams = useMemo(() => {
      const raw = new URLSearchParams(location.search).get("phone");
      if (!raw) return "";
      let digits = cleanPhone(raw).replace("+", "");
      if (digits.length === 11 && digits.startsWith("1")) {
        digits = digits.slice(1);
      }
      if (digits.length === 10) {
        return `+1${digits}`;
      }
      return "";
    }, [location.search]);

    const [formData, setFormData] = useState<FormData>({
      firstName: signupState?.userInfo?.firstName || "",
      lastName: signupState?.userInfo?.lastName || "",
      phone: signupState?.userInfo?.phone || phoneFromParams,
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [expandedCard, setExpandedCard] = useState<number | null>(null);
    const isComplete =
      !!formData.firstName.trim() &&
      !!formData.lastName.trim() &&
      !!formData.phone.trim();

    const setField = (field: keyof FormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

    const handleContinue = async () => {
      const nextErrors: FormErrors = {};
      if (!formData.firstName.trim()) {
        nextErrors.firstName = "First name is required";
      }
      if (!formData.lastName.trim()) {
        nextErrors.lastName = "Last name is required";
      }
      if (!formData.phone.trim()) {
        nextErrors.phone = "Phone is required";
      } else if (!checkPhone(formData.phone)) {
        nextErrors.phone = "Invalid phone number";
      }

      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        return;
      }

      await onNext?.({
        userInfo: {
          email: signupState?.userInfo?.email || "",
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          address: signupState?.userInfo?.address || "",
          timezone: signupState?.userInfo?.timezone || "",
        },
      });
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "center",
          alignItems: "center",
          padding: isMobile ? "0 16px" : "0 48px",
          gap: isMobile ? "24px" : "80px",
          width: "100%",
          minHeight: isMobile ? "auto" : "400px",
        }}
      >
        {/* Feature cards — hidden on mobile */}
        {!isMobile && (
          <Box
            sx={{
              flex: 1,
              maxWidth: "360px",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}
          >
            {features.map((f, i) => {
              const isOpen = expandedCard === i;
              return (
                <Box
                  key={f.title}
                  onClick={() => setExpandedCard(isOpen ? null : i)}
                  sx={{
                    background: "white",
                    borderRadius: "16px",
                    padding: "18px 20px",
                    boxShadow: isOpen
                      ? "0 4px 16px rgba(25,130,130,0.12)"
                      : "0 2px 8px rgba(0,0,0,0.05)",
                    cursor: "pointer",
                    transition: "box-shadow 0.2s, transform 0.2s",
                    "&:hover": {
                      boxShadow: "0 4px 16px rgba(25,130,130,0.12)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #E8F4F4 0%, #D4ECEC 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        color: "#198282",
                      }}
                    >
                      {f.icon}
                    </Box>
                    <Typography variant="bodyHeavy" sx={{ flex: 1 }}>
                      {f.title}
                    </Typography>
                    <ChevronDown
                      size={18}
                      color="#9CA3AF"
                      style={{
                        transition: "transform 0.2s",
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                  <Collapse in={isOpen}>
                    <Typography
                      variant="body"
                      sx={{
                        color: "#6B7280",
                        mt: "12px",
                        fontSize: "14px",
                        lineHeight: 1.5,
                      }}
                    >
                      {f.detail}
                    </Typography>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Profile form */}
        <Box
          sx={{
            flex: 1,
            maxWidth: "480px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <Typography variant={isMobile ? "h2" : "h1"}>
            Let's get started
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            We communicate primarily by text — start with your name and phone
            number.
          </Typography>

          {errors.general && <Alert severity="error">{errors.general}</Alert>}

          <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <TextField
              sx={{ flex: 1, minWidth: isMobile ? "100%" : "unset" }}
              label="First name"
              autoComplete="given-name"
              value={formData.firstName}
              error={!!errors.firstName}
              helperText={errors.firstName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField("firstName", e.target.value)
              }
            />
            <TextField
              sx={{ flex: 1, minWidth: isMobile ? "100%" : "unset" }}
              label="Last name"
              autoComplete="family-name"
              value={formData.lastName}
              error={!!errors.lastName}
              helperText={errors.lastName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField("lastName", e.target.value)
              }
            />
          </Box>

          <PhoneInput
            value={formData.phone}
            error={!!errors.phone}
            helperText={errors.phone}
            onChange={(phone) => {
              let newPhone = cleanPhone(
                typeof phone === "string" ? phone : phone.target.value,
              );
              if (newPhone.length >= 10 && !newPhone.startsWith("+1")) {
                newPhone = `+1${newPhone.replace("+", "")}`;
              }
              setField("phone", newPhone);
            }}
            label="Phone"
            name="phone"
            size="small"
            sx={{ width: "100%", marginTop: isMobile ? "-12px" : "0" }}
            autoComplete="tel"
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: isMobile ? "stretch" : "flex-end",
            }}
          >
            <NextStepButton
              hasValue={true}
              disabled={disabled || !isComplete}
              continueClick={handleContinue}
              buttonText="Get started"
              minWidth={isMobile ? "100%" : "180px"}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);
